import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

type LoadMoreFooterProps = {
    loadedCount: number;
    /** Patients matching the query server-side; may exceed those loaded. */
    totalCount: number;
    hasMore: boolean;
    onLoadMore: () => void;
    isLoadingMore: boolean;
    /** Whether the search term or a filter narrowed the query the total counts against. */
    isNarrowed: boolean;
};

/**
 * Loaded-of-total count and the next-page control, shown only while the list
 * is short of the cohort — a complete list speaks for itself, but a truncated
 * one must never read as the whole. Scrolling the footer into view loads the
 * next page; the button does the same without a pointer and when no
 * intersection is reported.
 *
 * Under a search or filter the total counts matches, not the cohort, so the
 * copy says "matching patients": the search box and chips that carry that
 * distinction are out of view by the time a reader has scrolled this far.
 */
export default function LoadMoreFooter({
    loadedCount,
    totalCount,
    hasMore,
    onLoadMore,
    isLoadingMore,
    isNarrowed,
}: LoadMoreFooterProps) {
    const sentinelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel || !hasMore || isLoadingMore) {
            return;
        }
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
                onLoadMore();
            }
        });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, isLoadingMore, onLoadMore]);

    // Guard on both: a page that comes back empty ends the walk with no next
    // page, and the list would still be short of the cohort.
    if (!hasMore && loadedCount >= totalCount) {
        return null;
    }

    return (
        <Box
            ref={sentinelRef}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                minHeight: 52,
                px: 3,
                borderTop: 1,
                borderColor: 'divider',
                bgcolor: 'background.raised',
            }}
        >
            <Typography variant="monoMeta" sx={{ fontSize: 13 }}>
                <FormattedMessage
                    id={
                        isNarrowed
                            ? 'patientList.loadedOfTotalFiltered'
                            : 'patientList.loadedOfTotal'
                    }
                    values={{ loadedCount, totalCount }}
                />
            </Typography>
            <Box sx={{ flex: 1 }} />
            {hasMore &&
                (isLoadingMore ? (
                    <CircularProgress size={14} />
                ) : (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={onLoadMore}
                    >
                        <FormattedMessage id="patientList.loadMore" />
                    </Button>
                ))}
        </Box>
    );
}
